import React from "react";
import {
  Box, Text, Flex, Center, VStack, HStack, Heading,
  Icon, SimpleGrid, Spinner,
} from "@chakra-ui/react";
import {
  FiShoppingBag, FiTag, FiPackage, FiMapPin, FiCalendar,
} from "react-icons/fi";
import { StoreImagesPanel } from "./StoreImagesPanel";

export function AbstractView({
  loadingStoreDetails,
  selectedStoreDetails,
  selectedStoreImages,
  currentImageIndex,
  setCurrentImageIndex,
  imageRotation,
  setImageRotation,
  onDownload,
}) {
  if (loadingStoreDetails) {
    return (
      <Center height="500px">
        <VStack spacing={3}>
          <Spinner size="xl" color="red.500" thickness="4px" speed="0.65s" />
          <Text color="gray.500" fontSize="sm">Loading store details...</Text>
        </VStack>
      </Center>
    );
  }

  if (!selectedStoreDetails) {
    return (
      <Center height="500px">
        <Text color="gray.600">Select a store to view details</Text>
      </Center>
    );
  }

  return (
    <Box height="100%" overflowY="auto">
      <Box mb={6}>
        <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={4}>
          Store Details
        </Text>

        <Flex direction={{ base: "column", md: "row" }} gap={4} align="stretch" mb={6}>
          {/* Store Info Card */}
          <Box
            p={4} bg="white" borderRadius="xl" border="1px solid"
            borderColor="gray.200" boxShadow="md" flex="1"
          >
            <Heading size="sm" mb={4} color="gray.800" fontWeight="bold">
              Store Information
            </Heading>
            <VStack align="start" spacing={3}>
              {[
                { icon: FiShoppingBag, color: "red.600",    label: "Store Name",   value: selectedStoreDetails?.storeName },
                { icon: FiTag,         color: "green.600",  label: "Store Type",   value: selectedStoreDetails?.storeType },
                { icon: FiPackage,     color: "purple.600", label: "Store Class",  value: selectedStoreDetails?.storeClass },
                { icon: FiPackage,     color: "yellow.600", label: "Store ID",     value: selectedStoreDetails?.storeId },
                { icon: FiCalendar,    color: "blue.600",   label: "Audited Date", value: selectedStoreDetails?.auditedDate },
              ].map(({ icon, color, label, value }) => (
                <HStack key={label} align="start" spacing={3}>
                  <Icon as={icon} color={color} fontSize="20px" />
                  <Box>
                    <Text fontSize="xs" color="gray.500" fontWeight="medium">{label}</Text>
                    <Text fontSize="sm" color="gray.800" fontWeight="semibold">{value || "-"}</Text>
                  </Box>
                </HStack>
              ))}
              {selectedStoreDetails.address && (
                <HStack align="start" spacing={3}>
                  <Icon as={FiMapPin} color="orange.600" fontSize="20px" />
                  <Box>
                    <Text fontSize="xs" color="gray.500" fontWeight="medium">Address</Text>
                    <Text fontSize="sm" color="gray.800" fontWeight="semibold">
                      {selectedStoreDetails.address}
                    </Text>
                  </Box>
                </HStack>
              )}
            </VStack>
          </Box>

          {/* Store Scores Card */}
          <Box
            p={4} bg="white" borderRadius="xl" border="1px solid"
            borderColor="gray.200" boxShadow="md" flex="1"
          >
            <Heading size="sm" mb={4} color="gray.800" fontWeight="bold">
              Store Scores
            </Heading>
            <SimpleGrid columns={1} spacing={3}>
              {[
                { label: "Availability",     value: selectedStoreDetails?.availabilityScore, bg: "green.50",  border: "green.200",  text: "green.700",  val: "green.600"  },
                { label: "Activation",       value: selectedStoreDetails?.activationScore,   bg: "yellow.50", border: "yellow.200", text: "yellow.700", val: "yellow.600" },
                { label: "Cooler",           value: selectedStoreDetails?.coolerScore,       bg: "red.50",    border: "red.200",    text: "red.700",    val: "red.600"    },
                { label: "Cooler Purity",    value: selectedStoreDetails?.coolerPurity,      bg: "blue.50",   border: "blue.200",   text: "blue.600",   val: "blue.600"   },
                { label: "Cooler Impurity",  value: selectedStoreDetails?.coolerImpurity,    bg: "gray.50",   border: "gray.200",   text: "gray.600",   val: "gray.600"   },
                { label: "Cooler Bonus",     value: selectedStoreDetails?.coolerBonus,       bg: "purple.50", border: "purple.200", text: "purple.700", val: "purple.600" },
              ].map(({ label, value, bg, border, text, val }) => (
                <Box
                  key={label} textAlign="center" p={3} bg={bg}
                  borderRadius="lg" border="1px solid" borderColor={border}
                >
                  <Text fontSize="sm" color={text} fontWeight="medium">{label}</Text>
                  <Text fontSize="lg" fontWeight="bold" color={val}>{value || 0}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        </Flex>
      </Box>

      {/* Store Images */}
      <Box>
        <Text fontSize="lg" fontWeight="bold" color="gray.800" mb={4}>
          Store Images
        </Text>
        <StoreImagesPanel
          selectedStoreImages={selectedStoreImages}
          currentImageIndex={currentImageIndex}
          setCurrentImageIndex={setCurrentImageIndex}
          imageRotation={imageRotation}
          setImageRotation={setImageRotation}
          onDownload={onDownload}
        />
      </Box>
    </Box>
  );
}
